import {
	createBrowserRouter,
	createMemoryRouter,
	RouterProvider,
} from 'react-router-dom';
import { staticIdentityRoutes } from '@ashn/identity-react/Identity';
import { useQuery } from 'react-query';
import { useMemo } from 'react';
import Layout from './components/shared/Layout';
import restDataService from './data/restDataService';
import getEnvironment from './utils/getEnvironment';
import mswStarterLogic from './utils/mswStarterLogic';
import { getIsAuthenticated } from './data/apiCalls';
import { LOGOUT_URL } from './scripts/navigationItems';
import PageNotFound from './components/PageNotFound/PageNotFound';
import {
	useRoutesList,
	unauthorizedCallback,
	postLoginUnauthorizedCallback,
} from './hooks/useRoutesList';

// Check for test environment
const isTestEnvironment =
	process.env.NODE_ENV === 'test' || typeof jest !== 'undefined';

const create = isTestEnvironment ? createMemoryRouter : createBrowserRouter;

const createRouter = (routes, isAuthenticated) =>
	create(
		[
			{
				path: 'identity',
				id: 'identity',
				errorElement: <PageNotFound />,
				loader: async () => {
					try {
						await mswStarterLogic();
						const { data } =
							await restDataService.get('auth/userparams');
						const { ssId, isAutoGeneratedUser } = data?.data || {};
						if (!ssId) return null;
						const env = getEnvironment();
						let redirectUri = `https://${env}.ashlink.com/auth/callback`;
						if (env === 'prod') {
							redirectUri =
								'https://www.ashlink.com/auth/callback';
						}
						return {
							apiEnvironment: env,
							clientId: 'ashlink.code',
							contactURL:
								'https://ashlink.com/ASH/help/contactus.aspx',
							customerServicePhoneNumber: '844-645-2746',
							customerServicePhoneNumberFitness: '844-645-2746',
							customerServiceOptionForFitness: '4',
							customGenericErrorMessage: `We're sorry, we are unable to confirm your eligibility. If you are a clinical sub-account user, the primary account must register before you can create your account. Please contact the primary account holder. If you feel this is incorrect, please contact us.<br/>For clinical Users:  844-645-2746<br/>For Fitness Users: 877-329-2746, option 4`,
							eligibilityURL: null,
							enableSSO: false,
							hasOwnProvider: false,
							ineligibleRegistration: false,
							logOutLink: LOGOUT_URL,
							overrideMainTitleTo: 'Login',
							path: 7,
							redirectUri,
							ssId,
							termsURL: '/termsconditions',
							isAuthenticated: isAuthenticated?.isLoggedIn,
							isAutoGeneratedUser,
							unauthorizedCallback,
							postLoginUnauthorizedCallback,
						};
					} catch {
						throw new Error('failed');
					}
				},
				element: <Layout />,
				children: [...staticIdentityRoutes],
			},
			...routes,
		],
		{ basename: '/clinical' }
	);

function App() {
	const getIsAuthenticatedResponse = useQuery(
		['isAuthenticated'],
		() => getIsAuthenticated(),
		{
			staleTime: Infinity,
		}
	);
	const isAuthenticated = useMemo(
		() => ({
			isLoading: getIsAuthenticatedResponse.isLoading,
			isLoggedIn: getIsAuthenticatedResponse?.data?.status === 200,
		}),
		[
			getIsAuthenticatedResponse?.isLoading,
			getIsAuthenticatedResponse?.data,
			getIsAuthenticatedResponse?.error,
		]
	);
	const routes = useRoutesList();
	const router = useMemo(
		() => createRouter(routes, isAuthenticated),
		[routes, isAuthenticated?.isLoggedIn]
	);
	return <RouterProvider router={router} />;
}

export default App;
